import Emitter from "@homer.core/emitter";
import api from "@homer.core/api";
import page from "@homer.core/page";
import clientState from "@homer.core/client.state";
import regexpEscape from "@homer.utility/regexp.escape";

const apiBus = api.Client.bus;
const instances = {};
const classes = document.documentElement.classList;

class Storage extends Emitter {
    #private;

    constructor(
        identifier,
        {
            version = undefined,
            lifetime = null,
            sticky = true,
            domain = null,
            path = "/",
            secure = true,
            samesite = "None",
            encode = true,
        },
    ) {
        if (instances[identifier]) {
            return instances[identifier];
        }

        super();

        this.#private = {
            identifier,
            version,
            lifetime,
            sticky,
            path,
            secure,
            samesite,
            domain,
            encode,
            // Match cookie names literally, including regex metacharacters.
            regex: new RegExp(`(?:^| )${regexpEscape(identifier)}=([^;]+)`),
            state: null,
        };

        this.#private.state = this.get();

        if (sticky) {
            globalThis.addEventListener("sendBeacon", () => {
                this.get();
            });

            apiBus.on("response", () => {
                this.get();
            });

            page.on(/^intent:(load|set)$/, () => {
                this.get();
            });

            clientState.on("leave", () => {
                this.get();
            });
        }

        instances[identifier] = this;
    }

    get() {
        const { version, regex, state, sticky } = this.#private;
        const [_, storageValue = undefined] = (document.cookie || "").match(regex) || [];
        let value;

        if (typeof storageValue !== "undefined") {
            const [cacheValue, cacheVersion] = storageValue.split(",");

            if (cacheVersion === version) {
                value = decodeURIComponent(cacheValue);
            }
        }

        if (sticky && typeof value === "undefined" && state) {
            value = state;

            this.set(value);
        }

        this.emit(value === state ? "hit" : "miss");

        return value === "undefined" ? undefined : value;
    }

    set(value) {
        if (classes.contains("isResetting")) {
            return;
        }

        const { identifier, version, lifetime, encode, path, samesite, domain, secure, regex, state } = this.#private;

        if (lifetime || value !== state || (document.cookie || "").match(regex) === null) {
            document.cookie = `${identifier}=${encode ? encodeURIComponent(value) : value}${
                version ? `,${version}` : ""
            };path=${path}${domain ? `;Domain=${domain}` : ""}${
                lifetime ? `;expires=${new Date(+new Date() + lifetime * 1000).toGMTString()}` : ""
            };SameSite=${samesite}${secure ? ";Secure" : ""}`;
        }

        if (value !== state) {
            this.#private.state = value;

            this.emit("change");
        }

        return this;
    }

    remove() {
        const { identifier, path, samesite, secure, sticky } = this.#private;

        if (sticky) {
            return this.set("undefined");
        }

        this.#private.state = undefined;

        document.cookie = `${identifier}=null;path=${path};expires=Thu, 01 Jan 1970 00:00:00 UTC;SameSite=${samesite}${
            secure ? ";Secure" : ""
        }`;

        this.emit("remove");

        return this;
    }
}

export default Storage;
